import React, { useState, useEffect, useCallback } from 'react';
import { getScheduledTaskPreview } from '../../utils/api';
import './EmailPreviewCard.css';

/**
 * EmailPreviewCard — in-chat preview of a scheduled task's email with a
 * markup flow. Implements the Claude Design scheduled-reports spec
 * (.epc block): chrome header with mail icon + subject, 340→760px frame
 * with bottom fade, borderless markup input with a circular send button.
 *
 * The assistant emits an ```email_preview fence after creating/updating a
 * scheduled task: { "taskId": "...", "taskName": "..." }. The card fetches
 * GET /aiScheduledTasks/preview (dry-run render — nothing is sent) and shows
 * the real email; typing a change ("remove the OTB table, show revenue as a
 * KPI") sends an update request as the user's next chat message.
 */

const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M3 21v-5h5" />
  </svg>
);

const PencilIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
  </svg>
);

const SendIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="m5 12 14 0" /><path d="m13 6 6 6-6 6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const EmailPreviewCard = ({ data, onAnswer }) => {
  const [state, setState] = useState({ loading: true, error: null, preview: null });
  const [markup, setMarkup] = useState('');
  const [sent, setSent] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const taskId = data?.taskId;
  const taskName = data?.taskName || data?.name || 'this task';

  const load = useCallback(async () => {
    setState({ loading: true, error: null, preview: null });
    try {
      const preview = await getScheduledTaskPreview(taskId);
      setState({ loading: false, error: null, preview });
    } catch (err) {
      setState({ loading: false, error: err?.response?.data?.error || err.message || 'Preview failed', preview: null });
    }
  }, [taskId]);

  useEffect(() => {
    if (taskId) load();
  }, [taskId, reloadKey, load]);

  if (!taskId) return null;

  const submitMarkup = () => {
    const text = markup.trim();
    if (!text || sent) return;
    setSent(true);
    if (typeof onAnswer === 'function') {
      onAnswer(`Update my scheduled task "${taskName}" (id ${taskId}): ${text}. Apply the changes, then show me the updated preview.`);
    }
  };

  return (
    <div className={`ai-email-preview-card${expanded ? ' is-expanded' : ''}`}>
      <div className="ai-email-preview-card__chrome">
        <div className="ai-email-preview-card__mailicon"><MailIcon /></div>
        <div className="ai-email-preview-card__titles">
          <div className="ai-email-preview-card__subject">
            {state.preview?.subject || taskName}
            <span className="ai-email-preview-card__badge">Preview</span>
          </div>
          <div className="ai-email-preview-card__sub">
            Rendered with live data · nothing has been sent
          </div>
        </div>
        <div className="ai-email-preview-card__actions">
          <button
            type="button"
            className={`ai-email-preview-card__btn${state.loading ? ' spinning' : ''}`}
            onClick={() => setReloadKey(k => k + 1)}
            title="Refresh preview"
          >
            <RefreshIcon />
          </button>
          <button type="button" className="ai-email-preview-card__btn" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      <div className="ai-email-preview-card__frame">
        {state.preview && (
          <iframe
            title={`Email preview — ${taskName}`}
            className="ai-email-preview-card__iframe"
            sandbox=""
            srcDoc={state.preview.html}
          />
        )}
        {state.loading && (
          <div className="ai-email-preview-card__regen">
            <div className="ai-email-preview-card__regen-orb" />
            Rendering preview…
          </div>
        )}
        {state.error && (
          <div className="ai-email-preview-card__error">Couldn't render the preview: {state.error}</div>
        )}
        <div className="ai-email-preview-card__fade" />
      </div>

      {sent ? (
        <div className="ai-email-preview-card__note">
          <CheckIcon /> Change requested — an updated preview will follow.
        </div>
      ) : (
        <div className="ai-email-preview-card__markup">
          <span className="ai-email-preview-card__markup-icon"><PencilIcon /></span>
          <input
            type="text"
            placeholder='Describe a change… "remove the OTB table, show total revenue as a KPI instead of the chart"'
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitMarkup(); }}
          />
          <button
            type="button"
            className={`ai-email-preview-card__markup-send${markup.trim() ? ' is-ready' : ''}`}
            onClick={submitMarkup}
            disabled={!markup.trim()}
            title="Request changes"
          >
            <SendIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailPreviewCard;
